import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { GlobalService, languagePreferences } from "../../../shared";
import { Edit, Globe, Trash2, Plus } from "lucide-react";
import Button from "../../../../components/ui/Button";
import { ConfirmPopup } from "primereact/confirmpopup";
import Card from "../../../../components/ui/card";
import {
    createGeneralClientSetting,
    deleteUserGeneralSetting,
    UpdateClientGeneralSettings,
    getUserGeneralSetting,
    useAppDispatch,
} from "../../../../redux";
import { useTranslation } from "react-i18next";
import LanguageDropdown from "../../../../components/ui/LanguageDropdown";

export default function LanguageComponent({
    isLanguageEditing,
    setIsLanguageEditing,
    editingLanguages,
    setEditingLanguages,
    startEditingLanguage,
    preferredLanguages,
    isCurrentUser = true,
    showEditIcon = true,
    canEdit = true,
}: any) {
    const { t } = useTranslation();
    const { user, timezones, currencies, languages } = useSelector((state: any) => state.auth);
    const dispatch = useAppDispatch();
    const deleteButtonRef = useRef<HTMLButtonElement>(null);
    const [deleteIndex, setDeleteIndex] = React.useState<number | null>(null);
    
    // Extract languages from allgeneralSetting.languageIds
    const [componentLanguages, setComponentLanguages] = React.useState([]);
    
    React.useEffect(() => {
        console.log("=== LanguageComponent preferredLanguages ===", preferredLanguages);
        if (preferredLanguages && preferredLanguages.length > 0) {
            preferredLanguages.forEach((lang, idx) => {
                console.log(`Language ${idx + 1} in component:`, {
                    language: lang.language,
                    languagePreferences: lang.languagePreferences,
                    languagePreferencesType: typeof lang.languagePreferences
                });
            });
            
            const extractedLanguages = [];
            preferredLanguages.forEach(lang => {
                if (lang.language) {
                    const existingLang = languages.find(l => l.id === lang.language);
                    if (existingLang && !extractedLanguages.find(el => el.id === existingLang.id)) {
                        extractedLanguages.push(existingLang);
                    }
                }
            });
            setComponentLanguages(extractedLanguages);
        }
    }, [preferredLanguages, languages]);

    const handleLanguageChange = (index: number, field: string, value: any) => {
        const updatedLanguages = [...editingLanguages];
        updatedLanguages[index] = {
            ...updatedLanguages[index],
            [field]: value,
        };
        setEditingLanguages(updatedLanguages);
    };

    const handleLanguageSelect = (index: number, language: any) => {
        const updatedLanguages = [...editingLanguages];
        updatedLanguages[index] = {
            ...updatedLanguages[index],
            language: language?.id || "",
            languageName: language?.name || "",
        };
        setEditingLanguages(updatedLanguages);
    };

    const addNewLanguage = () => {
        setEditingLanguages([
            ...editingLanguages,
            {
                language: "",
                languagePreferences: "",
            },
        ]);
    };

    const acceptDelete = async () => {
        if (deleteIndex === null) return;

        try {
            // Remove language from editing array
            const updatedLanguages = [...editingLanguages];
            updatedLanguages.splice(deleteIndex, 1);
            
            // If no languages left, reset to empty state
            if (updatedLanguages.length === 0) {
                setEditingLanguages([{ language: "", languagePreferences: "" }]);
                setDeleteIndex(null);
                return;
            }

            // Update the backend with remaining languages
            const currencyId = currencies.length > 0 ? (currencies[0]?.id || currencies[0]?.value) : "";
            const timezoneId = timezones.length > 0 ? (timezones[0]?.id || timezones[0]?.value) : "";
            const validLanguages = updatedLanguages.filter(lang => lang.language && lang.languagePreferences);

            if (validLanguages.length > 0) {
                const languageLevels = validLanguages.map(lang => ({
                    language: { id: String(lang.language) },
                    level: lang.languagePreferences
                }));

                const existingSettingId = preferredLanguages.length > 0 ? preferredLanguages[0].id : null;
                
                if (existingSettingId) {
                    const payload = {
                        userId: { id: user.id },
                        currencyId: { id: String(currencyId) },
                        timezoneId: { id: String(timezoneId) },
                        languageLevels: languageLevels,
                        languageIds: validLanguages.map(lang => ({ id: String(lang.language) }))
                    };

                    await dispatch(
                        UpdateClientGeneralSettings({
                            id: existingSettingId,
                            data: payload,
                        })
                    );
                }
            }

            setEditingLanguages(updatedLanguages);
            
            // Fetch updated general setting after delete
            await dispatch(getUserGeneralSetting({ id: user.id, isShowLoader: false }));
            
            GlobalService.showToast("success", t("common.success"), t("languageComponent.languageDeleted"));
        } catch (error) {
            GlobalService.showToast("error", t("common.error"), t("languageComponent.deleteFailed"));
            console.error("Error deleting language:", error);
        } finally {
            setDeleteIndex(null);
        }
    };

    const rejectDelete = () => {
        setDeleteIndex(null);
    };

    const confirmDelete = (event: React.MouseEvent, index: number) => {
        setDeleteIndex(index);
        deleteButtonRef.current = event.currentTarget as HTMLButtonElement;
    };

    const saveLanguages = async () => {
        const currencyId = currencies.length > 0 ? (currencies[0]?.id || currencies[0]?.value) : "";
        const timezoneId = timezones.length > 0 ? (timezones[0]?.id || timezones[0]?.value) : "";
        const validLanguages = editingLanguages.filter(lang => lang.language && lang.languagePreferences);

        if (validLanguages.length === 0) {
            GlobalService.showToast("warning", t("common.warning"), t("languageComponent.noValidLanguages"));
            return;
        }

        try {
            // Build languageLevels array with individual levels for each language
            const languageLevels = validLanguages.map(lang => ({
                language: { id: String(lang.language) },
                level: lang.languagePreferences
            }));

            // Check if we need to update or create
            const existingSettingId = preferredLanguages.length > 0 ? preferredLanguages[0].id : null;
            
            const payload = {
                userId: { id: user.id },
                currencyId: { id: String(currencyId) },
                timezoneId: { id: String(timezoneId) },
                languageLevels: languageLevels,
                languageIds: validLanguages.map(lang => ({ id: String(lang.language) }))
            };

            if (existingSettingId) {
                // Update existing general setting
                await dispatch(
                    UpdateClientGeneralSettings({
                        id: existingSettingId,
                        data: payload,
                    })
                );
                GlobalService.showToast("success", t("common.success"), t("languageComponent.settingUpdated"));
            } else {
                // Create new general setting
                await dispatch(
                    createGeneralClientSetting({
                        data: payload,
                    })
                );
                GlobalService.showToast("success", t("common.success"), t("languageComponent.settingCreated"));
            }
            
            // Fetch updated general setting after save
            await dispatch(getUserGeneralSetting({ id: user.id, isShowLoader: false }));
            
            setIsLanguageEditing(false);
        } catch (error) {
            GlobalService.showToast("error", t("common.error"), t("languageComponent.saveFailed"));
            console.error("Error saving languages:", error);
        }
    };

    const getLanguageLabel = (languageId: string) => {
        const lang = (componentLanguages.length > 0 ? componentLanguages : languages).find(l => String(l.id || l.value) === String(languageId));
        return lang ? lang.name : t("languageComponent.unknown");
    };

    // Helper function to get language name from languageIds array
    const getLanguageNameFromIds = (languageIds: any[]) => {
        if (!languageIds || languageIds.length === 0) return t("languageComponent.unknown");
        const firstLanguageId = languageIds[0]?.id;
        return getLanguageLabel(firstLanguageId);
    };

    const getPreferenceLabel = (value: string) => {
        console.log("=== getPreferenceLabel DEBUG ===");
        console.log("Input value:", value, "| Type:", typeof value);
        console.log("languagePreferences array:", languagePreferences);
        
        // Try exact match
        const pref = languagePreferences.find(p => p.value === value);
        console.log("Exact match result:", pref);
        
        // Try loose match
        const loosePref = languagePreferences.find(p => String(p.value) === String(value));
        console.log("Loose match result:", loosePref);
        
        const result = pref ? pref.label : t("languageComponent.unknown");
        console.log("Final result:", result);
        console.log("===========================");
        
        return result;
    };

    const shouldShowAddButton = () => {
        if (!isLanguageEditing) return false;
        const allHaveIds = editingLanguages.every(lang => lang.id);
        const lastLanguage = editingLanguages[editingLanguages.length - 1];
        const lastIsFilled = lastLanguage?.language && lastLanguage?.languagePreferences;
        return allHaveIds && lastIsFilled;
    };

    return (
        <Card color={undefined} className="p-4 w-full">
            <ConfirmPopup
                target={deleteButtonRef.current}
                visible={deleteIndex !== null}
                onHide={rejectDelete}
                message={t("languageComponent.confirmDelete")}
                icon="pi pi-exclamation-triangle"
                accept={acceptDelete}
                reject={rejectDelete}
                acceptLabel={t("languageComponent.yes")}
                rejectLabel={t("languageComponent.no")}
                acceptClassName="p-button-danger"
            />

            <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold mb-4">{t("languageComponent.title")}</h2>
                {!isLanguageEditing && isCurrentUser && showEditIcon && canEdit && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={startEditingLanguage}
                        className="text-blue-600 hover:text-blue-700">
                        <Edit className="h-4 w-4" />
                    </Button>
                )}
            </div>

            <div className="space-y-3">
                {!isLanguageEditing ? (
                    preferredLanguages.length > 0 ? (
                        preferredLanguages.map((language, index) => (
                            <div key={index} className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-gray-400" />
                                <span className="text-sm">
                                    {getLanguageLabel(language.language)} (
                                    {getPreferenceLabel(language.languagePreferences)})
                                </span>
                            </div>
                        ))
                    ) : (
                        <div className="text-gray-500 text-sm">{t("languageComponent.noLanguagesAdded")}</div>
                    )
                ) : isCurrentUser ? (
                    <>
                        {editingLanguages.map((lang, index) => (
                            <div key={index} className="space-y-3 border p-3 rounded-lg">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-sm font-medium">{t("languageComponent.languageNumber", { number: index + 1 })}</h3>
                                    {lang.language && lang.languagePreferences && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={e => confirmDelete(e, index)}
                                            className="text-red-600 hover:text-red-700">
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("languageComponent.language")}</label>
                                    <LanguageDropdown
                                        selectedLanguage={lang.language ? languages.find(l => (l.id || l.value) === lang.language) : null}
                                        onLanguageSelect={(language) => handleLanguageSelect(index, language)}
                                        placeholder={t("languageComponent.selectLanguage")}
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{t("languageComponent.proficiency")}</label>
                                    <select
                                        value={lang.languagePreferences}
                                        onChange={e =>
                                            handleLanguageChange(index, "languagePreferences", e.target.value)
                                        }
                                        className="w-full p-2 border rounded-lg">
                                        <option value="" disabled>
                                            {t("languageComponent.selectProficiency")}
                                        </option>
                                        {languagePreferences.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        ))}

                        <Button variant="outline" className="w-full" onClick={addNewLanguage}>
                            <Plus className="w-4 h-4 mr-2" />
                            {t("languageComponent.addAnotherLanguage")}
                        </Button>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="outline" className="text-sm" onClick={() => setIsLanguageEditing(false)}>
                                {t("languageComponent.cancel")}
                            </Button>
                            <Button className="text-sm" onClick={saveLanguages}>
                                {t("languageComponent.save")}
                            </Button>
                        </div>
                    </>
                ) : null}
            </div>
        </Card>
    );
}
